import React, { useContext } from 'react';
import {
  Button,
  Image,
  Link,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAcceptInvitationStyles } from '../styles';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

// "Learn more" target for the Microsoft work-or-school account explainer. Same Microsoft
// Advertising help host the pre-auth landing page uses for its sign-in help link.
const LEARN_MORE_URL = 'https://help.ads.microsoft.com/#apex/ads/en/60259/0';

export type ActivationWorkAccountRequiredProps = {
  // The work/school (AAD) account email this invitation's user is registered with. Shown inline so
  // the user knows which account to sign in with. Optional — omitted from the copy when unavailable.
  email?: string | null;
  // "Continue with Microsoft" — sign the current MSA/Google session out and send the user to the
  // work/school (AAD) sign-in.
  onContinueWithMicrosoft: () => void;
  // "Go back" — sign the current MSA/Google session out and return to the default invitation choice.
  onGoBack: () => void;
};

// Shown when ActivationOptions reports the invited email is registered with a Microsoft work or
// school (AAD) account, but the user signed in with a personal Microsoft (MSA) or Google account.
// To accept the invitation they must re-authenticate with the work/school account — both actions
// sign the current session out first (the URLs are resolved by the caller).
export const ActivationWorkAccountRequired: React.FC<ActivationWorkAccountRequiredProps> = ({
  email,
  onContinueWithMicrosoft,
  onGoBack,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  const accountLine = email
    ? i18n.getString(
      _TL_('Your Microsoft Advertising account for {{email}} uses a Microsoft work or school account (These are emails typically created for you by your IT department).'),
      { email }
    )
    : i18n.getString(_TL_('Your Microsoft Advertising account uses a Microsoft work or school account (These are emails typically created for you by your IT department).'));

  return (
    <div className={classes.centeredPage}>
      <div
        className={classes.errorCard}
        role="region"
        aria-labelledby="activation-work-required-title"
        aria-describedby="activation-work-required-description"
      >
        <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />

        <Title3 id="activation-work-required-title" className={classes.errorTitle}>
          {i18n.getString(_TL_('Sign in with Microsoft work or school account'))}
        </Title3>

        <div id="activation-work-required-description" className={classes.errorBody}>
          <Text block>{accountLine}</Text>
          <Text block>
            {i18n.getString(_TL_('Tip: When Microsoft asks which account to use, choose Work or school account.'))}
          </Text>
          <Text block>
            {i18n.getString(_TL_('To continue, sign in with your company or school account.'))}
            {' '}
            <Link href={LEARN_MORE_URL} target="_blank" rel="noopener noreferrer">
              {i18n.getString(_TL_('Learn More'))}
            </Link>
          </Text>
        </div>

        <div className={classes.errorActions}>
          <Button
            className="work-account-continue"
            appearance="primary"
            onClick={onContinueWithMicrosoft}
          >
            {i18n.getString(_TL_('Continue with Microsoft'))}
          </Button>
          <Button
            className="work-account-go-back"
            appearance="secondary"
            onClick={onGoBack}
          >
            {i18n.getString(_TL_('Go back'))}
          </Button>
        </div>
      </div>
    </div>
  );
};
